/**
 * Admin Assessment Edit Page
 * Create or edit an assessment with questions
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { assessmentsApi, questionsApi, questionBanksApi } from '../api';
import { Assessment, QuestionWithOptions, CreateQuestionInput, CreateAnswerOptionInput, QuestionBank, QuestionType } from '../../types/models';
import { QuestionEditor } from '../components/QuestionEditor.js';
import { AlertModal } from '../components/AlertModal';
import { MediaImage } from '../components/MediaImage';

const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  multiple_choice: 'Multiple Choice',
  multi_select: 'Multi Select',
  true_false: 'True/False',
  short_answer: 'Short Answer',
  matching: 'Matching',
};

const getQuestionTypeLabel = (type: QuestionType): string => QUESTION_TYPE_LABELS[type];

export const AdminAssessmentEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [assessment, setAssessment] = useState<Partial<Assessment>>({
    title: '',
    description: '',
    instructions: '',
    time_limit_seconds: null,
    passing_score_percent: null,
    shuffle_questions: false,
    shuffle_options: false,
    is_proctored: true,
    result_settings: {
      show_score_and_percentage: true,
      show_best_categories: false,
      show_worst_categories: false,
      num_categories_to_show: 3,
    },
    status: 'draft',
    source_type: 'manual',
  });
  const [questions, setQuestions] = useState<QuestionWithOptions[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<QuestionWithOptions | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [linkedQuestionBanks, setLinkedQuestionBanks] = useState<QuestionBank[]>([]);
  const [availableQuestionBanks, setAvailableQuestionBanks] = useState<QuestionBank[]>([]);
  const [showLinkQuestionBankModal, setShowLinkQuestionBankModal] = useState(false);
  const [linkingBank, setLinkingBank] = useState(false);
  const [linkQuestionsToSelect, setLinkQuestionsToSelect] = useState<Record<string, number | null>>({});
  const [questionsToSelectValues, setQuestionsToSelectValues] = useState<Record<string, string>>({});
  const [questionBankSearch, setQuestionBankSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadAssessment = async () => {
    if (!id || isNew) return;
    
    try {
      setLoading(true);
      const { questions: questionsData = [], ...assessmentFields } = await assessmentsApi.getById(id);
      setAssessment(assessmentFields);
      setQuestions(questionsData);
      
      // Load linked question banks
      const banks = await questionBanksApi.getByAssessmentId(id);
      setLinkedQuestionBanks(banks);
      
      // Initialize local state for questions_to_select inputs
      const initialValues: Record<string, string> = {};
      banks.forEach(bank => {
        initialValues[bank.id] = bank.questions_to_select?.toString() || '';
      });
      setQuestionsToSelectValues(initialValues);
    } catch (err: any) {
      setError(err.message || 'Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableQuestionBanks = async (search?: string) => {
    try {
      const result = await questionBanksApi.list({ limit: 100, search: search || undefined });
      const linkedIds = new Set(linkedQuestionBanks.map(b => b.id));
      const available = result.data.filter(b => !linkedIds.has(b.id));
      setAvailableQuestionBanks(available);
    } catch (err: any) {
      setError(err.message || 'Failed to load question banks');
    }
  };

  useEffect(() => {
    // Recalculate isNew based on current id
    const currentIsNew = !id || id === 'new';
    
    if (!currentIsNew && id) {
      loadAssessment();
    } else {
      // For new assessments, ensure loading is false
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (showLinkQuestionBankModal && !isNew) {
      loadAvailableQuestionBanks(questionBankSearch);
    }
  }, [showLinkQuestionBankModal, linkedQuestionBanks.length]);

  useEffect(() => {
    if (!showLinkQuestionBankModal) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      loadAvailableQuestionBanks(questionBankSearch);
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [questionBankSearch]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Double-check isNew based on current id value
      const currentIsNew = !id || id === 'new';
      
      if (currentIsNew) {
        // Create new assessment
        // Convert null values to undefined for optional fields
        const assessmentData: any = {
          ...assessment,
          created_by: '00000000-0000-0000-0000-000000000000', // Default user for POC
        };
        // Remove null values for optional fields (convert to undefined)
        if (assessmentData.time_limit_seconds === null) {
          assessmentData.time_limit_seconds = undefined;
        }
        if (assessmentData.passing_score_percent === null) {
          assessmentData.passing_score_percent = undefined;
        }
        if (assessmentData.description === null) {
          assessmentData.description = undefined;
        }
        if (assessmentData.instructions === null) {
          assessmentData.instructions = undefined;
        }
        if (assessmentData.source_reference === null) {
          assessmentData.source_reference = undefined;
        }
        if (assessmentData.result_settings === null || Object.keys(assessmentData.result_settings || {}).length === 0) {
          assessmentData.result_settings = undefined;
        }
        
        const newAssessment = await assessmentsApi.create(assessmentData);
        const assessmentId = newAssessment.id;
        navigate(`/admin/assessments/${assessmentId}`, { replace: true });
      } else {
        // Update existing assessment
        if (!id) {
          setError('Assessment ID is required for update');
          return;
        }
        const assessmentData: any = { ...assessment };

        await assessmentsApi.update(id, assessmentData);
        await loadAssessment();
      }
    } catch (err: any) {
      setAlert({ message: err.message || 'Failed to save assessment', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditQuestion = (question: QuestionWithOptions) => {
    setEditingQuestion(question);
    setShowQuestionForm(true);
    // Scroll to question form
    setTimeout(() => {
      const formElement = document.querySelector('.question-editor');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const handleUpdateQuestion = async (questionData: CreateQuestionInput, options: CreateAnswerOptionInput[]) => {
    if (!editingQuestion) return;
    
    try {
      // Extract questionId - ensure it's a string
      const questionId = String(editingQuestion.id);
      if (!questionId || questionId === 'undefined' || questionId === 'null') {
        setError('Invalid question: missing or invalid ID');
        return;
      }
      
      const assessmentId = isNew ? (assessment as any).id : id;
      if (!assessmentId) {
        setError('Please save the assessment first before updating questions');
        return;
      }

      // Update question (explicitly exclude any options field that might have been included)
      // Only include the fields that are allowed for question updates
      const updateData: any = {};
      if (questionData.question_type !== undefined) updateData.question_type = questionData.question_type;
      if (questionData.prompt_text !== undefined) updateData.prompt_text = questionData.prompt_text;
      if (questionData.points_possible !== undefined) updateData.points_possible = questionData.points_possible;
      if (questionData.explanation !== undefined) updateData.explanation = questionData.explanation;
      if (questionData.category !== undefined) updateData.category = questionData.category;
      if (questionData.allow_partial_credit !== undefined) {
        updateData.allow_partial_credit = questionData.allow_partial_credit;
      }
      
      // Explicitly ensure options is NOT included
      delete updateData.options;
      delete updateData.answer_options;
      
      await questionsApi.update(questionId, updateData);

      // Get existing option IDs
      const existingOptionIds = new Set(editingQuestion.answer_options.map(opt => opt.id));
      const newOptionIds = new Set(options.map((opt: any) => (opt as any)?.id).filter((id: any) => id && id !== ''));

      // Update or create options
      for (let i = 0; i < options.length; i++) {
        const opt = options[i];
        const optionId = (opt as any).id;
        
        // Ensure partial_credit_weight is valid
        let partialCreditWeight = opt.partial_credit_weight;
        if (partialCreditWeight !== undefined && partialCreditWeight !== null) {
          partialCreditWeight = Number.parseFloat(partialCreditWeight as any);
          if (Number.isNaN(partialCreditWeight) || partialCreditWeight < 0 || partialCreditWeight > 1) {
            throw new Error(`Invalid partial_credit_weight for option ${i + 1}: ${opt.partial_credit_weight}. Must be between 0 and 1.`);
          }
        }

        if (optionId && existingOptionIds.has(optionId)) {
          // Update existing option
          const updateData: any = {
            sequence_order: i + 1,
            option_text: opt.option_text || '',
          };
          
          if (opt.is_correct !== undefined) {
            updateData.is_correct = opt.is_correct;
          }
          
          if (partialCreditWeight !== undefined && partialCreditWeight !== null) {
            updateData.partial_credit_weight = partialCreditWeight;
          }
          
          if (opt.option_media_url) {
            updateData.option_media_url = opt.option_media_url;
            // Forward stored dimensions so the option preview reserves space.
            updateData.option_media_width = opt.option_media_width ?? null;
            updateData.option_media_height = opt.option_media_height ?? null;
          }

          if (opt.match_target) {
            updateData.match_target = opt.match_target;
          }

          await questionsApi.updateOption(questionId, optionId, updateData);
        } else {
          // Create new option - explicitly construct only the fields we need
          // Remove id, question_id, and any other unexpected fields
          const newOption: any = {
            sequence_order: i + 1,
            option_text: (opt.option_text || '').trim(),
          };
          
          // Only include is_correct if it's explicitly set (not undefined)
          if (opt.is_correct !== undefined) {
            newOption.is_correct = opt.is_correct;
          }
          
          // Only include partial_credit_weight if it has a valid value
          if (partialCreditWeight !== undefined && partialCreditWeight !== null) {
            newOption.partial_credit_weight = partialCreditWeight;
          }
          
          // Only include optional fields if they have values
          if (opt.option_media_url) {
            newOption.option_media_url = opt.option_media_url;
            newOption.option_media_width = opt.option_media_width ?? null;
            newOption.option_media_height = opt.option_media_height ?? null;
          }
          if (opt.match_target) {
            newOption.match_target = opt.match_target;
          }

          await questionsApi.createOptions(questionId, [newOption]);
        }
      }

      // Delete options that were removed
      for (const existingOpt of editingQuestion.answer_options) {
        if (!newOptionIds.has(existingOpt.id)) {
          await questionsApi.deleteOption(questionId, existingOpt.id);
        }
      }

      // Reload questions
      await loadAssessment();
      setShowQuestionForm(false);
      setEditingQuestion(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update question');
      // Don't continue if there was an error - this prevents deleting options on failure
      return;
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!confirm('Are you sure you want to delete this question?')) {
      return;
    }
    try {
      await questionsApi.delete(questionId);
      await loadAssessment();
      // Clear editing state if we deleted the question being edited
      if (editingQuestion?.id === questionId) {
        setEditingQuestion(null);
        setShowQuestionForm(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete question');
    }
  };

  const handlePublish = async () => {
    if (!id || id === 'new') {
      setAlert({ message: 'Please save the assessment first', type: 'warning' });
      return;
    }
    try {
      setPublishing(true);
      await assessmentsApi.publish(id);
      setAlert({ message: 'Assessment published successfully!', type: 'success' });
      await loadAssessment();
    } catch (err: any) {
      const errorMessage = err?.message || err?.error || 'Failed to publish assessment';
      setAlert({ message: errorMessage, type: 'error' });
    } finally {
      setPublishing(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!id || id === 'new') {
      setError('Please save the assessment first before uploading an image');
      return;
    }

    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // Validate file type - allow images including SVG
    const isValidImage = file.type.startsWith('image/') || 
                        file.type === 'image/svg+xml' ||
                        file.name.toLowerCase().endsWith('.svg');
    if (!isValidImage) {
      setError('Please select an image file (JPG, PNG, GIF, SVG, etc.)');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image file size must be less than 10MB');
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);
      const result = await assessmentsApi.uploadImage(id, file);
      setAssessment({ ...assessment, image_url: result.image_url });
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      // Reset file input
      input.value = '';
    }
  };

  const handleImageDelete = async () => {
    if (!id || id === 'new' || !assessment.image_url) return;

    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      setUploadingImage(true);
      setError(null);
      await assessmentsApi.deleteImage(id);
      setAssessment({ ...assessment, image_url: null });
    } catch (err: any) {
      setError(err.message || 'Failed to delete image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleLinkQuestionBank = async (questionBankId: string) => {
    if (!id || isNew) {
      setError('Please save the assessment first');
      return;
    }

    try {
      setLinkingBank(true);
      setError(null);
      // Get questionsToSelect value - convert null/0/empty to undefined so it's not sent
      const rawValue = linkQuestionsToSelect[questionBankId];
      const questionsToSelect = (rawValue && rawValue > 0) ? rawValue : undefined;
      await questionBanksApi.linkToAssessment(questionBankId, id, questionsToSelect);
      await loadAssessment(); // Reload to get updated questions and linked banks
      setShowLinkQuestionBankModal(false);
      setLinkQuestionsToSelect({}); // Reset
      setError(null); // Clear any previous errors
    } catch (err: any) {
      const errorMessage = err?.message || err?.error || 'Failed to link question bank';
      setError(errorMessage);
      console.error('Error linking question bank:', err);
    } finally {
      setLinkingBank(false);
    }
  };

  const handleUnlinkQuestionBank = async (questionBankId: string) => {
    if (!id || isNew) {
      return;
    }

    if (!window.confirm('Are you sure you want to unlink this question bank? Questions from this bank will no longer appear in the assessment.')) {
      return;
    }

    try {
      setError(null);
      await questionBanksApi.unlinkFromAssessment(questionBankId, id);
      await loadAssessment(); // Reload to get updated questions and linked banks
    } catch (err: any) {
      setError(err.message || 'Failed to unlink question bank');
    }
  };

  const renderQuestionCard = useCallback((question: QuestionWithOptions, index: number) => {
    const isFromBank = question.question_bank_id !== null;
    return (
      <div key={question.id} className="question-card">
        <div className="question-header">
          <span className="question-number">Question {index + 1}</span>
          <span className="question-type">
            {getQuestionTypeLabel(question.question_type)}
          </span>
          <span className="question-points">{question.points_possible} points</span>
          <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
            {!isFromBank && (
              <>
                <button
                  onClick={() => handleEditQuestion(question)}
                  className="btn btn-small btn-secondary"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteQuestion(question.id)}
                  className="btn btn-small btn-danger"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
        <div className="question-content">
          <p><strong>Prompt:</strong> {question.prompt_text}</p>
          <div className="answer-options">
            <strong>Answer Options:</strong>
            <ul>
              {question.answer_options.map((opt) => (
                <li key={opt.id}>
                  {opt.option_text}
                  {opt.is_correct && <span className="correct-badge">✓ Correct</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }, [handleEditQuestion, handleDeleteQuestion]);

  const renderQuestionsList = () => {
    if (isNew) {
      return (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          color: '#7f8c8d',
          fontStyle: 'italic',
        }}>
          Save the assessment to start linking question banks and adding questions.
        </div>
      );
    }

    if (questions.length === 0) {
      return (
        <p className="empty-state">
          No questions yet. Link a question bank above to add questions to this assessment.
        </p>
      );
    }

    const groupedQuestions = questions.reduce((acc, question, index) => {
      const category = question.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push({ question, index });
      return acc;
    }, {} as Record<string, Array<{ question: QuestionWithOptions; index: number }>>);

    const sortedCategories = Object.keys(groupedQuestions).sort((a, b) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });

    return sortedCategories.map((category) => (
      <div key={category} className="question-category-group">
        <h3 className="category-header">{category}</h3>
        {groupedQuestions[category].map(({ question, index }) =>
          renderQuestionCard(question, index)
        )}
      </div>
    ));
  };

  if (loading) {
    return <div className="loading">Loading assessment...</div>;
  }

  return (
    <div className="admin-assessment-edit">
      <div className="admin-header">
        <h1>{isNew ? 'New Assessment' : 'Edit Assessment'}</h1>
        <div className="admin-actions">
          <Link to="/admin" className="btn btn-secondary">
            Back to Dashboard
          </Link>
          <button type="button" onClick={handleSave} disabled={saving || publishing} className="btn btn-primary">
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
          {!isNew && assessment.status === 'draft' && (
            <button type="button" onClick={handlePublish} disabled={saving || publishing} className="btn btn-primary">
              {publishing ? 'Publishing...' : 'Publish'}
            </button>
          )}
        </div>
      </div>

      {/* Only show error at top for non-alert errors (like loading errors) */}
      {error && !alert && <div className="error">{error}</div>}

      <div className="assessment-form">
        <div className="form-group">
          <label htmlFor="assessment-title">
            Title <span className="required">*</span>
          </label>
          <input
            id="assessment-title"
            type="text"
            value={assessment.title || ''}
            onChange={(e) => setAssessment({ ...assessment, title: e.target.value })}
            placeholder="Assessment title"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="assessment-description">Description</label>
          <textarea
            id="assessment-description"
            value={assessment.description || ''}
            onChange={(e) => setAssessment({ ...assessment, description: e.target.value })}
            placeholder="Assessment description"
            rows={3}
          />
        </div>

        <div className="form-group">
          <label htmlFor="assessment-instructions">Instructions</label>
          <textarea
            id="assessment-instructions"
            value={assessment.instructions || ''}
            onChange={(e) => setAssessment({ ...assessment, instructions: e.target.value })}
            placeholder="Instructions for students"
            rows={4}
          />
        </div>

        <div className="form-group">
          <label>Assessment Image</label>
          {isNew ? (
            <div style={{ 
              marginTop: '0.5rem', 
              padding: '1rem', 
              backgroundColor: '#e8f4f8', 
              border: '1px solid #3498db', 
              borderRadius: '4px',
              color: '#2c3e50'
            }}>
              <strong>💡 Save this assessment first</strong>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                After saving, you'll be able to upload an image to display on the assessment list and during the assessment.
              </p>
            </div>
          ) : (
            <>
              <p className="form-help-text">Upload an image to display on the assessment list and during the assessment.</p>
              {assessment.image_url ? (
                <div className="image-preview-container">
                  <div className="image-preview">
                    <MediaImage
                      src={assessment.image_url}
                      alt="Assessment preview"
                      width={assessment.image_width}
                      height={assessment.image_height}
                    />
                  </div>
                  <div className="image-actions">
                    <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                      {uploadingImage ? 'Uploading...' : 'Replace Image'}
                      <input
                        type="file"
                        accept="image/*,.svg"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={handleImageDelete}
                      disabled={uploadingImage}
                      className="btn btn-secondary"
                    >
                      Delete Image
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                    {uploadingImage ? 'Uploading...' : 'Upload Image'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              )}
            </>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="assessment-time-limit">Time Limit (minutes)</label>
            <input
              id="assessment-time-limit"
              type="number"
              value={assessment.time_limit_seconds ? Math.floor(assessment.time_limit_seconds / 60) : ''}
              onChange={(e) => {
                const minutes = Number.parseInt(e.target.value) || 0;
                setAssessment({
                  ...assessment,
                  time_limit_seconds: minutes > 0 ? minutes * 60 : null,
                });
              }}
              placeholder="Leave empty for unlimited"
              min="1"
            />
          </div>

          <div className="form-group">
            <label htmlFor="assessment-passing-score">Passing Score (%)</label>
            <input
              id="assessment-passing-score"
              type="number"
              value={assessment.passing_score_percent || ''}
              onChange={(e) => {
                const percent = Number.parseFloat(e.target.value) || null;
                setAssessment({
                  ...assessment,
                  passing_score_percent: percent,
                });
              }}
              placeholder="Leave empty for no pass/fail"
              min="0"
              max="100"
              step="0.1"
            />
          </div>

        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={assessment.shuffle_questions || false}
                onChange={(e) => setAssessment({ ...assessment, shuffle_questions: e.target.checked })}
              />
              {' '}Shuffle Questions
            </label>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={assessment.shuffle_options || false}
                onChange={(e) => setAssessment({ ...assessment, shuffle_options: e.target.checked })}
              />
              {' '}Shuffle Answer Options
            </label>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={assessment.is_proctored !== false}
                onChange={(e) => setAssessment({ ...assessment, is_proctored: e.target.checked })}
              />
              {' '}Proctored assessment
            </label>
            <small style={{ display: 'block', marginTop: '0.25rem', color: '#7f8c8d' }}>
              Check this box if this assessment requires proctoring. Proctored assessments will ask the student 
              to enter their proctor key within app.pathful.com before being redirected to assessments.pathful.com.
            </small>
          </div>
        </div>

        <div className="form-section">
          <h3>Result Display Settings</h3>
          <p className="form-help-text">The results page always shows completion status and date. Configure additional information to display below.</p>
          
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={assessment.result_settings?.show_score_and_percentage !== false}
                onChange={(e) => setAssessment({
                  ...assessment,
                  result_settings: {
                    ...assessment.result_settings,
                    show_score_and_percentage: e.target.checked,
                  },
                })}
              />
              {' '}Show Score and Percentage (e.g., "8/10 (80%)")
            </label>
            <small style={{ display: 'block', marginTop: '0.25rem', color: '#7f8c8d' }}>
              When unchecked, students will only see a completion message without scores.
            </small>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={assessment.result_settings?.show_best_categories === true}
                onChange={(e) => setAssessment({
                  ...assessment,
                  result_settings: {
                    ...assessment.result_settings,
                    show_best_categories: e.target.checked,
                  },
                })}
              />
              {' '}Show Best Performing Categories
            </label>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={assessment.result_settings?.show_worst_categories === true}
                onChange={(e) => setAssessment({
                  ...assessment,
                  result_settings: {
                    ...assessment.result_settings,
                    show_worst_categories: e.target.checked,
                  },
                })}
              />
              {' '}Show Areas for Improvement (Worst Categories)
            </label>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={assessment.result_settings?.show_all_categories === true}
                onChange={(e) => setAssessment({
                  ...assessment,
                  result_settings: {
                    ...assessment.result_settings,
                    show_all_categories: e.target.checked,
                  },
                })}
              />
              {' '}Show All Category Results
            </label>
            <small style={{ display: 'block', marginTop: '0.25rem', color: '#7f8c8d' }}>
              Display all category scores without showing individual question details.
            </small>
          </div>

          {(assessment.result_settings?.show_best_categories || assessment.result_settings?.show_worst_categories) && (
            <div className="form-group">
              <label>
                Number of Categories to Show{' '}
                <input
                  type="number"
                  value={assessment.result_settings?.num_categories_to_show || 3}
                  onChange={(e) => setAssessment({
                    ...assessment,
                    result_settings: {
                      ...assessment.result_settings,
                      num_categories_to_show: Number.parseInt(e.target.value) || 3,
                    },
                  })}
                  min="1"
                  max="10"
                  style={{ marginLeft: '0.5rem', width: '60px' }}
                />
              </label>
            </div>
          )}

          <div className="form-group" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #dee2e6' }}>
            <label>
              <input
                type="checkbox"
                checked={assessment.result_settings?.show_question_review === true}
                onChange={(e) => setAssessment({
                  ...assessment,
                  result_settings: {
                    ...assessment.result_settings,
                    show_question_review: e.target.checked,
                    // If question review is disabled, disable sub-options
                    show_correct_answers: e.target.checked ? assessment.result_settings?.show_correct_answers : false,
                    show_explanations: e.target.checked ? assessment.result_settings?.show_explanations : false,
                  },
                })}
              />
              {' '}Show Question Review (display questions and user responses after completion)
            </label>
            <small style={{ display: 'block', marginTop: '0.25rem', color: '#7f8c8d' }}>
              When enabled, students can review their responses after completing the assessment.
            </small>
          </div>

          {assessment.result_settings?.show_question_review && (
            <>
              <div className="form-group" style={{ marginLeft: '2rem' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={assessment.result_settings?.show_correct_answers === true}
                    onChange={(e) => setAssessment({
                      ...assessment,
                      result_settings: {
                        ...assessment.result_settings,
                        show_correct_answers: e.target.checked,
                      },
                    })}
                  />
                  {' '}Show Correct Answers
                </label>
                <small style={{ display: 'block', marginTop: '0.25rem', color: '#7f8c8d' }}>
                  When enabled, students will see which answers were correct and incorrect.
                </small>
              </div>

              <div className="form-group" style={{ marginLeft: '2rem' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={assessment.result_settings?.show_explanations === true}
                    onChange={(e) => setAssessment({
                      ...assessment,
                      result_settings: {
                        ...assessment.result_settings,
                        show_explanations: e.target.checked,
                      },
                    })}
                  />
                  {' '}Show Explanations
                </label>
                <small style={{ display: 'block', marginTop: '0.25rem', color: '#7f8c8d' }}>
                  When enabled, students will see explanations for each question (if provided).
                </small>
              </div>
            </>
          )}

          <div className="form-group" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #dee2e6' }}>
            <label htmlFor="post-assessment-instructions">Post-Assessment Instructions</label>
            <textarea
              id="post-assessment-instructions"
              value={assessment.result_settings?.post_assessment_instructions || ''}
              placeholder="Shown to students after they complete the assessment"
              rows={3}
              onChange={(e) => setAssessment({
                ...assessment,
                result_settings: {
                  ...assessment.result_settings,
                  post_assessment_instructions: e.target.value || undefined,
                },
              })}
            />
            <small style={{ display: 'block', marginTop: '0.25rem', color: '#7f8c8d' }}>
              Displayed above the completion button. To add a link, use the format [link text](https://example.com).
            </small>
          </div>

          <div className="form-group" style={{ marginTop: '1.5rem' }}>
            <h4 style={{ marginBottom: '0.5rem' }}>Completion Button</h4>
            <small style={{ display: 'block', marginBottom: '0.75rem', color: '#7f8c8d' }}>
              Customize the button shown after a student completes the assessment.
            </small>
            <label htmlFor="completion-button-text">Button Text</label>
            <input
              id="completion-button-text"
              type="text"
              value={assessment.result_settings?.completion_button_text || ''}
              placeholder="Return to Dashboard"
              onChange={(e) => setAssessment({
                ...assessment,
                result_settings: {
                  ...assessment.result_settings,
                  completion_button_text: e.target.value || undefined,
                },
              })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="completion-button-url">Button URL</label>
            <input
              id="completion-button-url"
              type="text"
              value={assessment.result_settings?.completion_button_url || ''}
              placeholder="/"
              onChange={(e) => setAssessment({
                ...assessment,
                result_settings: {
                  ...assessment.result_settings,
                  completion_button_url: e.target.value || undefined,
                },
              })}
            />
            <small style={{ display: 'block', marginTop: '0.25rem', color: '#7f8c8d' }}>
              Can be a relative path (e.g. /assessments) or an external URL (e.g. https://pathful.com).
            </small>
          </div>
        </div>
      </div>

      {!isNew && (
        <div className="form-section" style={{ marginTop: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3>Question Banks</h3>
            <button
              onClick={async () => {
                setShowLinkQuestionBankModal(true);
                // Load available question banks when opening the modal
                if (!isNew) {
                  await loadAvailableQuestionBanks();
                }
              }}
              className="btn btn-secondary"
            >
              Link Question Bank
            </button>
          </div>
          <p className="form-help-text">
            Link question banks to include their questions in this assessment. Questions from banks are read-only here.
          </p>
          
          {linkedQuestionBanks.length === 0 ? (
            <p style={{ color: '#7f8c8d', fontStyle: 'italic' }}>No question banks linked. Click "Link Question Bank" to add one.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {linkedQuestionBanks.map((bank) => (
                <div
                  key={bank.id}
                  style={{
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <strong>{bank.name}</strong>
                      {bank.description && (
                        <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginTop: '0.25rem' }}>
                          {bank.description}
                        </div>
                      )}
                      <div style={{ fontSize: '0.85rem', color: '#7f8c8d', marginTop: '0.25rem' }}>
                        {bank.question_count ?? 0} questions available
                      </div>
                    </div>
                    <button
                      onClick={() => handleUnlinkQuestionBank(bank.id)}
                      className="btn btn-small btn-danger"
                    >
                      Unlink
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                      Randomly select:{' '}
                      <input
                        type="number"
                        min="1"
                        max={bank.question_count ?? 0}
                        value={questionsToSelectValues[bank.id] ?? (bank.questions_to_select?.toString() || '')}
                        onChange={(e) => {
                          // Update local state only - no API call
                          setQuestionsToSelectValues({
                            ...questionsToSelectValues,
                            [bank.id]: e.target.value,
                          });
                        }}
                        onBlur={async (e) => {
                          // Save when user leaves the field
                          const value = e.target.value === '' ? null : Number.parseInt(e.target.value);
                          if (value !== null && (Number.isNaN(value) || value < 1)) {
                            // Reset to current value if invalid
                            setQuestionsToSelectValues({
                              ...questionsToSelectValues,
                              [bank.id]: bank.questions_to_select?.toString() || '',
                            });
                            return;
                          }
                          try {
                            await questionBanksApi.updateQuestionsToSelect(bank.id, id!, value);
                            // Update the bank in local state without full reload
                            setLinkedQuestionBanks(linkedQuestionBanks.map(b => 
                              b.id === bank.id ? { ...b, questions_to_select: value } : b
                            ));
                          } catch (err: any) {
                            setError(err.message || 'Failed to update questions to select');
                            // Reset to current value on error
                            setQuestionsToSelectValues({
                              ...questionsToSelectValues,
                              [bank.id]: bank.questions_to_select?.toString() || '',
                            });
                          }
                        }}
                        onKeyDown={(e) => {
                          // Save on Enter key
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        placeholder="All"
                        style={{
                          marginLeft: '0.5rem',
                          width: '80px',
                          padding: '0.25rem 0.5rem',
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                        }}
                      />
                      <span style={{ marginLeft: '0.25rem', fontSize: '0.85rem', color: '#7f8c8d' }}>
                        questions from this bank (leave empty for all)
                      </span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showLinkQuestionBankModal && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => {
            setShowLinkQuestionBankModal(false);
            setQuestionBankSearch('');
          }}
        >
          <div
            aria-modal="true"
            aria-label="Link Question Bank"
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '8px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Link Question Bank</h2>
              <button
                onClick={() => {
                  setShowLinkQuestionBankModal(false);
                  setQuestionBankSearch('');
                }}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>

            <input
              type="text"
              value={questionBankSearch}
              onChange={(e) => setQuestionBankSearch(e.target.value)}
              placeholder="Search question banks..."
              autoFocus
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                marginBottom: '1rem',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '0.95rem',
                boxSizing: 'border-box',
              }}
            />

            {error && (
              <div className="error" style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#ffebee', border: '1px solid #f44336', borderRadius: '4px', color: '#c62828' }}>
                {error}
              </div>
            )}
            
            {availableQuestionBanks.length === 0 ? (
              <p style={{ color: '#7f8c8d', fontStyle: 'italic' }}>
                {questionBankSearch
                  ? `No question banks found matching "${questionBankSearch}".`
                  : 'No available question banks to link. All question banks are already linked, or there are no question banks yet.'}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {availableQuestionBanks.map((bank) => (
                  <div
                    key={bank.id}
                    style={{
                      padding: '1rem',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <div style={{ flex: 1 }}>
                        <strong>{bank.name}</strong>
                        {bank.description && (
                          <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginTop: '0.25rem' }}>
                            {bank.description}
                          </div>
                        )}
                        <div style={{ fontSize: '0.85rem', color: '#7f8c8d', marginTop: '0.25rem' }}>
                          {bank.question_count ?? 0} questions available
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <label style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                        Randomly select:{' '}
                        <input
                          type="number"
                          min="1"
                          max={bank.question_count ?? 0}
                          value={linkQuestionsToSelect[bank.id] || ''}
                          onChange={(e) => {
                            const value = e.target.value === '' ? null : Number.parseInt(e.target.value);
                            if (value !== null && (Number.isNaN(value) || value < 1)) return;
                            setLinkQuestionsToSelect({ ...linkQuestionsToSelect, [bank.id]: value });
                          }}
                          placeholder="All"
                          style={{
                            marginLeft: '0.5rem',
                            width: '80px',
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                          }}
                        />
                        <span style={{ marginLeft: '0.25rem', fontSize: '0.85rem', color: '#7f8c8d' }}>
                          questions (leave empty for all)
                        </span>
                      </label>
                    </div>
                    <button
                      onClick={() => handleLinkQuestionBank(bank.id)}
                      disabled={linkingBank}
                      className="btn btn-primary"
                    >
                      {linkingBank ? 'Linking...' : 'Link'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="questions-section">
        <div className="questions-header">
          <h2>Questions ({questions.length})</h2>
          {isNew ? (
            <div style={{ 
              marginTop: '0.5rem', 
              padding: '1rem', 
              backgroundColor: '#e8f4f8', 
              border: '1px solid #3498db', 
              borderRadius: '4px',
              color: '#2c3e50'
            }}>
              <strong>💡 Save this assessment first</strong>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                After saving, you'll be able to link question banks to add questions to this assessment. Questions are added by linking question banks, not individually.
              </p>
            </div>
          ) : (
            <p className="form-help-text" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
              Questions are added by linking question banks above.
            </p>
          )}
        </div>

        {showQuestionForm && editingQuestion && !isNew && (
          <QuestionEditor
            question={editingQuestion}
            onSave={handleUpdateQuestion}
            onCancel={() => {
              setShowQuestionForm(false);
              setEditingQuestion(null);
            }}
          />
        )}

        <div className="questions-list">
          {renderQuestionsList()}
        </div>
      </div>

      <AlertModal
        isOpen={!!alert}
        onClose={() => setAlert(null)}
        message={alert?.message || ''}
        type={alert?.type || 'info'}
      />
    </div>
  );
};
